export const stocksIndices = {
    id: 'stocks-indices',
    title: 'Stocks and indices',
    summary: 'What moves a share price, how earnings season works, how an index is built, why stocks gap at the open, and how a single share differs from an index.',
    level: 'intermediate',
    art: 'usd',
    image: { src: '/academy/stocks-indices.webp', alt: 'A green dollar coin beside rising glass bar columns on a dark floor' },
    screen: { to: '/markets', label: 'Markets' },
    lessons: [
        {
            id: 'what-moves-a-share-price',
            title: 'What moves a share price',
            minutes: 4,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'A share is a small piece of a company. Its price is what buyers and sellers agree it is worth right now, based on what they expect the company to earn in the future.' },
                {
                    type: 'list',
                    items: [
                        '**Company results**: profits, sales and the outlook management gives.',
                        '**Company news**: a new product, a lawsuit, a change of boss, a takeover offer.',
                        '**Interest rates**: higher rates make future profits worth less today, which tends to weigh on shares, especially fast-growing ones.',
                        '**The economy**: growth, jobs and inflation figures change what investors expect from every company at once.',
                        '**The mood of the whole market**: on a broad sell-off, good companies fall too.',
                    ],
                },
                { type: 'p', text: 'What matters is the surprise, not the news itself. A company that earns a record profit can still fall if investors expected even more.' },
                { type: 'example', title: 'A stock trade in numbers', text: 'You buy 10 Apple shares at $228, a $2,280 trade. Apple rises to $234 and you close. Profit: 10 × $6 = **$60**, before the spread, any commission and swap. If it had fallen to $222, you would have lost $60.' },
                { type: 'warn', text: 'A single company can fall a long way on one piece of news. Size a stock trade for that, not for an average day.' },
            ],
            quiz: [
                {
                    q: 'A company reports a record profit, but the share falls. What is the most likely reason?',
                    options: ['The data is wrong', 'Investors expected even more', 'Record profits always make shares fall'],
                    answer: 1,
                    why: 'Prices move on the gap between results and expectations, not on the result alone.',
                },
                {
                    q: 'You buy 5 shares at $100 and close at $96. What is the result before costs?',
                    options: ['A $4 loss', 'A $20 loss', 'A $20 profit'],
                    answer: 1,
                    why: '5 shares × the $4 fall = $20 lost.',
                },
            ],
            tryIt: { to: '/trade/AAPL', label: 'Open Apple on the trade screen' },
            terms: ['earnings', 'notional'],
        },
        {
            id: 'earnings-season',
            title: 'Earnings season: what to watch',
            minutes: 5,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'Listed companies report their results every quarter. Most large US companies report within a few weeks of each other, a stretch called **earnings season**.' },
                {
                    type: 'list',
                    items: [
                        '**Earnings per share (EPS)**: profit divided by the number of shares. It is compared with the average analyst estimate.',
                        '**Revenue**: total sales. A profit beat on falling sales is often treated with caution.',
                        '**Guidance**: what the company expects for the next quarter or year. It can move the price more than the past quarter does.',
                    ],
                },
                { type: 'p', text: 'When the data is available, click a stock on the Markets page to open its quick view. The **Company** section shows the **Next earnings** date, marked **before open** or **after close**, the **EPS estimate**, and third-party analyst ratings and price targets. These are other people’s views, not ours, and not a reason to trade.' },
                { type: 'example', title: 'A beat that falls', text: 'Analysts expect EPS of $1.50. The company reports $1.60, a beat of about 7%. But it cuts its sales outlook for next year, and the share drops 5% at the next open.' },
                { type: 'tip', text: 'Big reports also move the index the company belongs to, and sometimes other companies in the same industry.' },
                { type: 'warn', text: 'Reports usually come out when the US stock market is closed, so the first price you can trade at may be far from the last close.' },
            ],
            quiz: [
                {
                    q: 'What is EPS compared with when a company reports?',
                    options: ['The share price', 'The average analyst estimate', 'Last year’s dividend'],
                    answer: 1,
                    why: 'The market reacts to how EPS compares with what analysts expected.',
                },
                {
                    q: 'Where can you see a stock’s next earnings date, when it is available?',
                    options: ['In the stock’s quick view on the Markets page', 'In Account → Security', 'Only on the company’s website'],
                    answer: 0,
                    why: 'The Company section of the quick view shows the next earnings date and whether it is before the open or after the close.',
                },
                {
                    q: 'A company beats its EPS estimate but lowers its guidance. What can happen?',
                    options: ['The share must rise', 'The share can still fall', 'Trading is stopped'],
                    answer: 1,
                    why: 'Guidance is about the future, and it often matters more than the quarter that has passed.',
                },
            ],
            tryIt: { to: '/markets', label: 'Open a stock’s quick view' },
            terms: ['earnings', 'eps', 'gap'],
        },
        {
            id: 'how-an-index-is-built',
            title: 'How an index is built',
            minutes: 5,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'An **index** follows a basket of shares in one number. When you trade an index, you trade the whole basket in one go.' },
                {
                    type: 'list',
                    items: [
                        '**US 500**: 500 large US companies.',
                        '**US Tech 100**: 100 of the largest non-financial companies on the Nasdaq exchange, heavy in technology.',
                        '**Wall Street 30**: 30 large US companies.',
                        '**US Small Cap 2000**: 2,000 smaller US companies.',
                        'Outside the US: **Germany 40 (DAX)**, **UK 100 (FTSE)**, **France 40 (CAC)**, **Euro Stoxx 50**, **Japan 225 (Nikkei)**, **Hong Kong 50 (Hang Seng)**, **Australia 200 (ASX)** and **China A50**.',
                    ],
                },
                { type: 'p', text: 'Most indices are weighted by **market value** (share price × number of shares). The biggest companies count the most, so a handful of giants can move the whole US 500. The Wall Street 30 is different: it is weighted by **share price**, so a $500 share moves it more than a $50 share, whatever the size of the company.' },
                { type: 'example', title: 'One company, one index', text: 'A company makes up 7% of an index weighted by market value. Its share rises 10% and everything else is flat. The index rises about 7% × 10% = **0.7%**.' },
                { type: 'p', text: 'Indices quoted in another currency, such as the Germany 40 in euros, are shown here in $ at the live exchange rate.' },
                { type: 'tip', text: 'Use the Indices filter on the Markets page to see every index you can trade and whether it is open.' },
            ],
            quiz: [
                {
                    q: 'In an index weighted by market value, which company moves it most?',
                    options: ['The one with the highest share price', 'The one with the largest market value', 'Each company counts the same'],
                    answer: 1,
                    why: 'Weight follows market value, so the largest companies have the biggest effect.',
                },
                {
                    q: 'A company is 5% of an index. Its share rises 4% and the rest are flat. How much does the index move?',
                    options: ['About 0.2%', 'About 4%', 'About 5%'],
                    answer: 0,
                    why: '5% × 4% = 0.2%.',
                },
            ],
            tryIt: { to: '/trade/SPX500', label: 'Open the US 500' },
            terms: ['index', 'index-weighting', 'asset-class'],
        },
        {
            id: 'open-close-overnight-gaps',
            title: 'The open, the close and overnight gaps',
            minutes: 5,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'US stocks here trade in the regular US session: **09:30 to 16:00 New York time**, Monday to Friday. Outside those hours you cannot open or close a stock trade.' },
                { type: 'p', text: 'US indices keep much longer hours: from Sunday 18:00 to Friday 17:00 New York time, with a one-hour break each day. Other indices follow their own markets; the Hong Kong 50, for example, pauses for lunch.' },
                { type: 'p', text: 'News does not wait for the open. When a company reports after the close, the share can open far from where it closed. That jump is a **gap**.' },
                { type: 'example', title: 'A gap through a stop', text: 'You hold a buy on a stock that closed at $228, with a stop loss at $220. The company reports after the close and the share opens at $210. Your stop closes the trade at the first available price, about $210, not $220. On 10 shares that is a $180 loss instead of $80.' },
                { type: 'p', text: 'Meanwhile the US Tech 100 is still trading, so the same news can show up in the index hours before the share can be traded.' },
                { type: 'warn', text: 'A stop loss does not protect you from a gap. Before you hold a stock overnight, check whether it has earnings or other news coming.' },
            ],
            quiz: [
                {
                    q: 'When can you trade US stocks here?',
                    options: ['24 hours a day', '09:30 to 16:00 New York time, Monday to Friday', 'Only at weekends'],
                    answer: 1,
                    why: 'Stocks follow the regular US session.',
                },
                {
                    q: 'A share closes at $50. Your stop is at $48. It opens at $45. Where does the stop close the trade?',
                    options: ['$48', 'About $45, the first available price', 'It stays open'],
                    answer: 1,
                    why: 'After a gap a stop fills at the next available price, which can be worse than your level.',
                },
            ],
            tryIt: { to: '/markets', label: 'Check which markets are open' },
            terms: ['gap', 'trading-hours', 'stop-loss'],
        },
        {
            id: 'sectors-and-rotation',
            title: 'Sectors and rotation',
            minutes: 4,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'Companies are grouped into **sectors** by what they do. Shares in the same sector often move together, because the same news affects them.' },
                {
                    type: 'list',
                    items: [
                        '**Technology**: Apple, Microsoft, NVIDIA, AMD, Intel, Oracle.',
                        '**Financials**: JPMorgan Chase, Bank of America, Visa, Mastercard.',
                        '**Health care**: Eli Lilly, UnitedHealth, Johnson & Johnson, Pfizer.',
                        '**Consumer staples**, things people buy in any economy: Walmart, Costco, Procter & Gamble, Coca-Cola.',
                        '**Energy**: Exxon Mobil, which tends to move with oil.',
                    ],
                },
                { type: 'p', text: '**Sector rotation** is money moving from one group to another as the outlook changes. When investors expect strong growth, they often favour technology and other fast-growing companies. When they worry, they often move toward consumer staples and health care, whose sales hold up better in a slowdown.' },
                { type: 'example', title: 'Same index, different day', text: 'The US 500 is flat on the day. Underneath, technology shares are down 1.5% and consumer staples are up 1.2%. Nothing changed at index level, but money rotated between sectors.' },
                { type: 'tip', text: 'Make a watchlist for each sector you follow on the Markets page. It shows at a glance which group is leading.' },
            ],
            quiz: [
                {
                    q: 'Why do shares in the same sector often move together?',
                    options: ['They are the same company', 'The same news affects them', 'The platform links their prices'],
                    answer: 1,
                    why: 'Companies in one industry share customers, costs and news, so their shares often react together.',
                },
                {
                    q: 'An index is flat, but tech shares fall and consumer staples rise. What is this an example of?',
                    options: ['A gap', 'Sector rotation', 'A stop-out'],
                    answer: 1,
                    why: 'Money is moving between sectors, even though the index as a whole hardly moved.',
                },
            ],
            tryIt: { to: '/markets', label: 'Build a sector watchlist' },
            terms: ['sector-rotation', 'correlation'],
        },
        {
            id: 'single-stock-vs-index',
            title: 'Single stock vs index: different risk',
            minutes: 4,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'A trade on one company depends on that company. A trade on an index spreads the same money across many companies, so bad news at one of them moves it far less.' },
                {
                    type: 'compare',
                    left: { title: 'Single stock', items: ['Moves on its own news and earnings', 'Can gap hard on one report', 'Trades in the US session only', 'Often a lower maximum leverage'] },
                    right: { title: 'Index', items: ['Moves with the market as a whole', 'One company’s news is diluted', 'US indices trade almost around the clock', 'Moves on rates, economy and big companies'] },
                },
                { type: 'example', title: 'The same bad report', text: 'A company that makes up 3% of the US 500 falls 20% after its results. A $2,000 trade on the share loses **$400**. A $2,000 trade on the US 500 loses about 3% × 20% = 0.6%, or **$12**, if the rest of the index is flat.' },
                { type: 'p', text: 'Each market has its own spread, leverage range and swap rates. Every trade pays the spread; some asset classes also charge a commission on open and close, others none. The order panel shows what applies before you place an order, and the **Max leverage** column on the Markets page compares markets.' },
                { type: 'warn', text: 'An index is spread across many companies, but it is still leveraged. A broad sell-off moves every share at once.' },
            ],
            quiz: [
                {
                    q: 'One company in an index falls sharply. Which trade loses more, for the same size?',
                    options: ['A trade on that company’s share', 'A trade on the index', 'They lose the same'],
                    answer: 0,
                    why: 'In the index the company is only a small part of the basket, so its fall is diluted.',
                },
                {
                    q: 'Where do you see the costs of a market before you trade it?',
                    options: ['In the order panel', 'Only after the trade closes', 'In the glossary'],
                    answer: 0,
                    why: 'The order panel shows the spread, fee and margin for the market you have open.',
                },
            ],
            tryIt: { to: '/trade/NAS100', label: 'Compare the US Tech 100 with a stock' },
            terms: ['index', 'spread', 'leverage', 'swap'],
        },
    ],
};
export const stocksIndicesTerms = [
    { id: 'earnings', term: 'Earnings', text: 'A company’s quarterly results: profit, sales and its outlook. Prices react to how they compare with what analysts expected.', lesson: 'earnings-season' },
    { id: 'eps', term: 'EPS (earnings per share)', text: 'A company’s profit divided by its number of shares. Compared with the analyst estimate when results come out.', lesson: 'earnings-season' },
    { id: 'index', term: 'Index', text: 'One price that follows a basket of shares, such as the US 500. Most are weighted by market value, so the largest companies count the most.', lesson: 'how-an-index-is-built' },
    { id: 'index-weighting', term: 'Index weighting', text: 'How much each company counts in an index. Most indices weight by market value (share price × number of shares); the Wall Street 30 weights by share price.', lesson: 'how-an-index-is-built' },
    { id: 'gap', term: 'Gap', text: 'A jump between one price and the next, often between a market’s close and its next open. A stop loss then fills at the first available price.', lesson: 'open-close-overnight-gaps' },
    { id: 'sector-rotation', term: 'Sector rotation', text: 'Money moving from one group of companies to another, for example from technology to consumer staples, as the economic outlook changes.', lesson: 'sectors-and-rotation' },
];
//# sourceMappingURL=stocks-indices.js.map